import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Card } from '@blueprintjs/core';
import './AboutPage.scss';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-page__hero section--gradient">
        <div className="container">
          <div className="about-page__hero-content">
            <span className="about-page__eyebrow">Meet Your Agent</span>
            <h1>Rolita Cargill</h1>
            <p>Living Benefits Specialist</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section">
        <div className="container">
          <div className="about-page__about">
            <div className="about-page__photo">
              <div className="about-page__photo-placeholder">
                <Icon icon="person" size={80} />
              </div>
            </div>
            <div className="about-page__bio">
              <h2>Protecting Families Through Modern Insurance</h2>
              <p>
                Hello! I'm Rolita Cargill, and I'm passionate about helping families
                understand and access the life-changing protection that Living Benefits
                life insurance provides.
              </p>
              <p>
                After seeing too many families struggle financially during medical crises —
                even when they had traditional life insurance — I knew there had to be a
                better way. That's when I discovered Living Benefits, and it changed
                everything about how I approach family protection.
              </p>
              <p>
                Traditional life insurance was designed for a different era. Today, advances
                in medicine mean more people survive serious illnesses like cancer, heart
                attacks, and strokes. But surviving isn't free — the medical bills, lost
                income, and ongoing care costs can devastate a family's finances.
              </p>
              <p>
                <strong>Living Benefits bridges this gap.</strong> It's life insurance that
                works while you're still alive, providing tax-free access to your death
                benefit when you need it most — not just when you die.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section section--light">
        <div className="container">
          <div className="about-page__mission">
            <h2>My Mission</h2>
            <p className="about-page__mission-statement">
              To spread awareness about Living Benefits life insurance and ensure every
              family has access to protection that truly works — for living and for legacy.
            </p>
            <div className="about-page__values">
              <Card className="about-page__value-card">
                <Icon icon="learning" size={32} />
                <h3>Education First</h3>
                <p>
                  I believe in empowering you with knowledge. Understanding your options
                  is the first step to making the right decision for your family.
                </p>
              </Card>
              <Card className="about-page__value-card">
                <Icon icon="heart" size={32} />
                <h3>Family Focused</h3>
                <p>
                  Your family's well-being is my priority. I take time to understand
                  your unique situation and needs before making any recommendations.
                </p>
              </Card>
              <Card className="about-page__value-card">
                <Icon icon="lock" size={32} />
                <h3>Trust & Transparency</h3>
                <p>
                  No pressure, no gimmicks. I provide honest guidance and clear
                  explanations so you can make confident decisions.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <h2>How I Can Help</h2>
            <p className="about-page__section-subtitle">
              I specialize in solutions that protect families during life's
              most challenging moments.
            </p>
          </div>
          <div className="about-page__services">
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="shield" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Living Benefits Life Insurance</h3>
                <p>
                  Access your death benefit while still alive if diagnosed with
                  critical illness, chronic illness, or critical injury. Protection
                  that works when you need it.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="home" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Mortgage Protection</h3>
                <p>
                  Keep your family in their home if something happens to you.
                  Policies designed specifically to cover your mortgage obligations.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="bank-account" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Tax-Free Retirement</h3>
                <p>
                  Explore life insurance strategies that can provide tax-advantaged
                  income in retirement — another living benefit of properly structured
                  policies.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="briefcase" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Small Business Planning</h3>
                <p>
                  Protect your business with key person insurance, buy-sell agreements,
                  and succession planning that includes Living Benefits.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="inherited-group" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Estate Planning</h3>
                <p>
                  Ensure your legacy is protected and your wishes are carried out
                  with proper life insurance structuring and beneficiary planning.
                </p>
              </div>
            </div>
            <div className="about-page__service">
              <div className="about-page__service-icon">
                <Icon icon="people" size={28} />
              </div>
              <div className="about-page__service-content">
                <h3>Family Protection Review</h3>
                <p>
                  Not sure if you're properly protected? I offer free reviews of
                  your current coverage to identify gaps and opportunities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Me Section */}
      <section className="section section--light">
        <div className="container">
          <div className="about-page__why-me">
            <h2>Why Work With Me?</h2>
            <div className="about-page__reasons">
              <div className="about-page__reason">
                <Icon icon="tick-circle" size={24} />
                <div>
                  <h4>Specialized Expertise</h4>
                  <p>
                    Living Benefits is my focus. I stay current on the best products
                    and carriers offering these modern protections.
                  </p>
                </div>
              </div>
              <div className="about-page__reason">
                <Icon icon="tick-circle" size={24} />
                <div>
                  <h4>Multiple Carriers</h4>
                  <p>
                    I work with multiple top-rated insurance companies to find the
                    best fit for your specific needs and budget.
                  </p>
                </div>
              </div>
              <div className="about-page__reason">
                <Icon icon="tick-circle" size={24} />
                <div>
                  <h4>No-Pressure Approach</h4>
                  <p>
                    My role is to educate and guide, not pressure. You'll never
                    feel pushed into a decision you're not ready to make.
                  </p>
                </div>
              </div>
              <div className="about-page__reason">
                <Icon icon="tick-circle" size={24} />
                <div>
                  <h4>Ongoing Support</h4>
                  <p>
                    I'm here for the long term. From application to claims support,
                    I'll be your advocate throughout your policy's life.
                  </p>
                </div>
              </div>
              <div className="about-page__reason">
                <Icon icon="tick-circle" size={24} />
                <div>
                  <h4>Free Consultations</h4>
                  <p>
                    There's never a charge to talk. Let's discuss your situation
                    and see if Living Benefits are right for you.
                  </p>
                </div>
              </div>
              <div className="about-page__reason">
                <Icon icon="tick-circle" size={24} />
                <div>
                  <h4>Nationwide Service</h4>
                  <p>
                    I serve clients across the country, helping families everywhere
                    access the protection they deserve.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--dark">
        <div className="container">
          <div className="about-page__cta">
            <h2>Let's Talk About Your Family's Protection</h2>
            <p>
              I'd love to learn about your situation and share how Living Benefits
              might help protect the people you love most.
            </p>
            <div className="about-page__cta-buttons">
              <Link to="/contact">
                <Button
                  intent="success"
                  large
                  rightIcon={<Icon icon="arrow-right" />}
                >
                  Schedule a Free Consultation
                </Button>
              </Link>
              <Link to="/living-benefits">
                <Button large outlined className="about-page__cta-secondary">
                  Learn About Living Benefits
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
